import { SELECTORS } from 'chameleon/packages/blue-book-player/constants/selectors';
import { HIGHLIGHT_COLORS_NAMES } from 'chameleon/packages/blue-book-player/Annotation/constants';
import {
  areInsideInnerContainer,
  isHighlightRestricted,
} from 'chameleon/packages/blue-book-player/InnerContainer/utils';

export const preventHighlightPopoverFlicker = (callback, playerEl) => {
  const popoverContainer = playerEl.querySelector(
    SELECTORS.ANNOTATION_POPOVER_CONTAINER,
  );
  popoverContainer.style.opacity = 1;
  popoverContainer.style.visibility = 'visible';
  callback();

  setTimeout(() => {
    popoverContainer.style.removeProperty('opacity');
    popoverContainer.style.removeProperty('visibility');
  }, 200);
};

export const isSelected = (currentColor, selectedColor) =>
  currentColor === selectedColor;

export const findActiveHighlightElement = (clickedElement) => {
  if (
    clickedElement?.classList?.contains(
      'lrn-annotations-texthighlight-marking--active',
    )
  ) {
    return clickedElement;
  }
  const activeElement = clickedElement.closest(
    '.lrn-annotations-texthighlight-marking--active',
  );

  return activeElement || null;
};

export const getClickedElementColor = (clickedElement) => {
  if (!clickedElement) return null;
  const activeElement = findActiveHighlightElement(clickedElement);
  if (!activeElement) return null;
  const elementClasses = activeElement.classList;
  if (
    elementClasses.contains(
      `lrn-annotations-color--${HIGHLIGHT_COLORS_NAMES.YELLOW}`,
    )
  ) {
    return HIGHLIGHT_COLORS_NAMES.YELLOW;
  }
  if (
    elementClasses.contains(
      `lrn-annotations-color--${HIGHLIGHT_COLORS_NAMES.BLUE}`,
    )
  ) {
    return HIGHLIGHT_COLORS_NAMES.BLUE;
  }
  if (
    elementClasses.contains(
      `lrn-annotations-color--${HIGHLIGHT_COLORS_NAMES.RED}`,
    )
  ) {
    return HIGHLIGHT_COLORS_NAMES.RED;
  }
  return null;
};

const cleanSelector = (selector) => {
  // Remove the "_<number>" at the end of the ID if it exists used for shared passages
  const modifiedSelector = selector.replace(/(id="[^"]*?)(_\d+)"/, '$1"');

  // Add ^ between the id keyword and the = sign to apply for all shared passages
  return modifiedSelector.replace(/id=/, 'id^=');
};

export const computeLearnosityHighlightInfo = (
  element,
  highlightModule,
  playerEl,
) => {
  const highlightedElement = findActiveHighlightElement(element);
  const highlightedElements = playerEl.querySelectorAll(
    `x-marker[data-lrn-texthighlight-id="${highlightedElement.dataset.lrnTexthighlightId}"][data-is-highlight-node="true"]`,
  );
  if (!highlightedElements.length) return null;

  if (highlightedElements.length === 1) {
    const { originalIndex } = highlightedElements[0].dataset;
    const startContainerPath = `${highlightModule.utils.getPath(highlightedElement)};${originalIndex}`;
    const endContainerPath = `${highlightModule.utils.getPath(highlightedElement)};${originalIndex}`;
    return {
      startContainerPath: cleanSelector(startContainerPath),
      endContainerPath: cleanSelector(endContainerPath),
      startOffset: parseInt(highlightedElement.dataset.originalOffsetStart, 10),
      endOffset:
        parseInt(highlightedElement.dataset.originalOffsetStart, 10) +
        highlightedElement.innerText.length,
    };
  }

  const [startElement] = highlightedElements;
  const endElement = highlightedElements[highlightedElements.length - 1];
  const startContainerPath = `${highlightModule.utils.getPath(startElement)};${startElement.dataset.originalIndex}`;
  const endContainerPath = `${highlightModule.utils.getPath(endElement)};${endElement.dataset.originalIndex}`;
  return {
    startContainerPath: cleanSelector(startContainerPath),
    endContainerPath: cleanSelector(endContainerPath),
    startOffset: parseInt(startElement.dataset.originalOffsetStart, 10),
    endOffset:
      parseInt(endElement.dataset.originalOffsetStart, 10) +
      endElement.innerText.length,
  };
};

const getHighlightedClickedElementText = (clickedElement) => {
  const highlightId = clickedElement.getAttribute('data-lrn-texthighlight-id');
  const matchingElements = document.querySelectorAll(
    `[data-lrn-texthighlight-id="${highlightId}"][data-is-highlight-node="true"]`,
  );
  const matchingElementsArray = Array.from(matchingElements);
  return matchingElementsArray.map((element) => element.innerText).join('');
};

const getHighlightedClickedElementId = (clickedElement) =>
  clickedElement.getAttribute('data-lrn-texthighlight-id');

export const computeHighlightData = (
  clickedElement,
  highlightInfo,
  learnosityItemReference,
) => ({
  id: getHighlightedClickedElementId(clickedElement),
  item: learnosityItemReference,
  path: highlightInfo.endContainerPath,
  offset: highlightInfo.startOffset,
  selection: getHighlightedClickedElementText(clickedElement, highlightInfo),
  color: getClickedElementColor(clickedElement),
});

export const getAnnotationId = (items, target) => {
  const { color, item, offset, path, selection } = target;

  const matchingItem = items.find(
    ({
      highlight: {
        color: itemColor,
        item: itemItem,
        offset: itemOffset,
        path: itemPath,
        selection: itemSelection,
      },
    }) =>
      itemColor === color &&
      itemItem === item &&
      itemOffset === offset &&
      itemPath === path &&
      itemSelection === selection,
  );

  return matchingItem ? matchingItem.id : null;
};

export const isHighlightActive = () =>
  !!document.querySelector('.lrn-annotations-c-button--removeHighlight');

export const canAnnotate = (selection) => {
  const anchorNode = selection?.anchorNode?.parentNode;
  const focusNode = selection?.focusNode?.parentNode;
  return (
    areInsideInnerContainer(anchorNode, focusNode) &&
    !isHighlightRestricted(anchorNode) &&
    !isHighlightRestricted(focusNode)
  );
};
